import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from './components/Layout/Layout';
import BackOfficeCommercialCompanyStep from './components/BackOfficeCommercialCompanyStep';
import BackOfficeTechnicalDataStep from './components/BackOfficeTechnicalDataStep';
import BackOfficePersonalDataStep from './components/BackOfficePersonalDataStep';
import generateFiscalCode from '../../common/helpers/generateFiscalCode';
import ProgressBar from '../../common/componentsV2/ProgressBar';
import { generalAlertError } from '../../common/utils/alerts';
import {
  commercialCompanyDataSchema,
  commercialDefaultValues,
  commercialTechnicalDataSchema,
  personalDataSchema,
  vatNumberValidationSchema,
} from '../Commercial/utils';
import { validateProduct95Guarantees } from '../Commercial/validations/validationFunctions';
import {
  commercialStepFieldsById,
  NON_TOUCHABLE_FIELDS,
  isCommercialFieldValid,
  getBlockingFields,
  buildMissingFieldsMessage,
} from '../Commercial/validations/commercialStepFields';
import { modifyRequestDataBackoffice } from '../../services/axios-client/axeCommons';
import PageLoading from '../../common/componentsV2/elements/PageLoading';
import { backOfficeCommercialInitialData, commercialSubmitAnswers, resetCommercialForm } from '../../features/formsBackoffice/formsBackofficeActions';
import { V2FontImport } from '../../common/formInputsV2/V2Fonts';
import { FormLayout } from '../../common/formInputsV2/FormLayout.styles';
import { FooterNav } from '../../common/formInputsV2/FooterNav.styles';
import { PrimaryButton, SecondaryButton } from '../../common/formInputsV2/Button.styles';
import { sonnerErrorAlert } from '../../common/utils/sonnerAlerts';
import QuoteGenerateLoading from '../../common/componentsV2/elements/QuoteGenerateLoading';

const StepIdType = {
  COMMERCIAL_COMPANY: 'commercial_company',
  TECHNICAL_DATA: 'technical_data',
  PERSONAL_DATA: 'personal_data',
};

export function BackOfficeCommercial() {
  const { requestToken } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [isModifyState, setIsModifyState] = useState(false);
  const [showPrivacyErrors, setShowPrivacyErrors] = useState(false);

  const {
    municipalities, states, isSubmitting, dataPrivacy,
  } = useSelector(
    (store) => store.formsBackoffice.commercial,
  );
  const {
    register,
    formState: { errors, touchedFields },
    setValue,
    trigger,
    watch,
    control,
  } = useForm({
    mode: 'onTouched',
    resolver: yupResolver(
      [
        vatNumberValidationSchema,
        commercialCompanyDataSchema,
        commercialTechnicalDataSchema,
        personalDataSchema,
      ].reduce((acc, schema) => acc.concat(schema)),
    ),
    shouldFocusError: false,
    defaultValues: commercialDefaultValues,
  });

  const formData = watch();

  const isValid = (name) => isCommercialFieldValid(errors, touchedFields, name);

  const scrollToFirstError = () => {
    setTimeout(() => {
      const el = document.querySelector(
        '.is-invalid, [aria-invalid="true"], .general-input-error, .invalid-feedback.d-block',
      );
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  };

  const autoValidatePrefilledFields = async (fields) => {
    const filled = (fields || []).filter((name) => {
      const value = watch(name);
      return value !== undefined && value !== null && value !== '';
    });

    if (filled.length === 0) return;

    filled
      .filter((name) => !NON_TOUCHABLE_FIELDS.includes(name))
      .forEach((name) => {
        setValue(name, watch(name), { shouldTouch: true, shouldValidate: false });
      });

    await trigger(filled);
  };

  const steps = [
    {
      id: StepIdType.COMMERCIAL_COMPANY,
      title: 'Completa i Dati',
    },
    {
      id: StepIdType.TECHNICAL_DATA,
      title: 'Dati Tecnici',
    },
    {
      id: StepIdType.PERSONAL_DATA,
      title: 'Dati Contatto',
    },
  ];

  const updateFormData = (data) => {
    const keys = Object.keys(data);
    keys.forEach((key) => {
      setValue(key, data[key], {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    });
    trigger(keys);
  };

  const privacyItemChangeHandler = (value, item) => {
    if (!value.target.checked) {
      const newArray = [...formData.privacies];
      const index = newArray.indexOf(item.id);
      newArray.splice(index, 1);
      updateFormData({
        privacies: newArray,
      });
      return;
    }
    const answersNewArray = [...formData.privacies];
    answersNewArray.push(item.id);
    updateFormData({
      privacies: answersNewArray,
    });
  };

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const step = steps[currentStepIndex];

  useEffect(() => {
    autoValidatePrefilledFields(commercialStepFieldsById[step.id] || []);
  }, [currentStepIndex]);

  const renderStepComponent = () => {
    switch (step.id) {
      case StepIdType.COMMERCIAL_COMPANY:
        return (
          <BackOfficeCommercialCompanyStep
            register={register}
            errors={errors}
            isValid={isValid}
            setValue={setValue}
            control={control}
            formData={formData}
            isModifyState={isModifyState}
          />
        );
      case StepIdType.TECHNICAL_DATA:
        return (
          <BackOfficeTechnicalDataStep
            register={register}
            watch={watch}
            errors={errors}
            isValid={isValid}
            setValue={setValue}
            control={control}
          />
        );
      case StepIdType.PERSONAL_DATA:
        return (
          <BackOfficePersonalDataStep
            register={register}
            watch={watch}
            errors={errors}
            isValid={isValid}
            setValue={setValue}
            control={control}
            formData={formData}
            dataPrivacy={dataPrivacy}
            privacyItemChangeHandler={privacyItemChangeHandler}
            showPrivacyErrors={showPrivacyErrors}
          />
        );
      default:
        return null;
    }
  };

  const validateDataPrivacy = () => {
    let { error, message } = '';

    dataPrivacy.forEach((privacy) => {
      if (privacy.required === 1) {
        const userDataArray = { ...formData }; // make a separate copy of the object
        const index = userDataArray.privacies.indexOf(privacy.id);

        if (index < 0) {
          error = true;
          message = 'Tutti i campi sono obbligatori! Tutta la privacy con la stella deve essere controllata';
        }
      }
    });
    return { error, message };
  };

  const handleNextStep = () => {
    let stepValidation = null;
    let stepFields = {};
    switch (step.id) {
      case StepIdType.COMMERCIAL_COMPANY:
        stepValidation = commercialCompanyDataSchema;
        stepFields = commercialStepFieldsById.commercial_company;
        break;
      case StepIdType.TECHNICAL_DATA: {
        stepValidation = commercialTechnicalDataSchema;
        stepFields = commercialStepFieldsById.technical_data;
        break;
      }
      case StepIdType.PERSONAL_DATA:
        stepValidation = personalDataSchema;
        stepFields = commercialStepFieldsById.personal_data;
        break;
      default:
        sonnerErrorAlert('Step non riconosciuto.');
        return;
    }

    if (stepValidation) {
      const isValidStep = stepValidation.isValidSync(formData);
      if (!isValidStep) {
        const blocking = getBlockingFields(stepValidation, formData, stepFields);
        trigger(stepFields, { shouldFocus: true }).then(() => {
          sonnerErrorAlert(buildMissingFieldsMessage(blocking));
          scrollToFirstError();
        });
        return;
      }
    }

    // Guarantee-dependency validation only makes sense once the basic
    // technical fields are actually filled in — tpab_value in particular
    // is what lets NobisCoveragesSection auto-add FABB/RILO and satisfy
    // most of these dependencies before the user ever sees this error.
    // Runs after the generic step validation above, not before it.
    if (step.id === StepIdType.TECHNICAL_DATA) {
      const validationResult = validateProduct95Guarantees(formData, {
        assetNumber: 1,
      });

      if (validationResult.hasBlockingErrors()) {
        const errorMessages = validationResult.errors
          .map((err) => `• ${err.message}`)
          .join('\n');

        generalAlertError(
          `Correggere i seguenti errori prima di procedere:\n\n${errorMessages}`,
        );
        return;
      }

      if (validationResult.hasWarnings()) {
        // eslint-disable-next-line no-console
        console.warn('Validation warnings:', validationResult.warnings);
      }
    }

    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      const validation = validateDataPrivacy();
      if (validation.error) {
        setShowPrivacyErrors(true);
        sonnerErrorAlert(validation.message);
        scrollToFirstError();
        return;
      }

      if (formData.gender != 'G') {
        const d = formData.date_of_birth.split('-');

        const fData = {
          name: formData.name,
          surname: formData.surname,
          gender: formData.gender,
          day: d[2],
          month: d[1],
          year: d[0],
        };

        if (formData.born_abroad) {
          fData.birthplace = states.filter(
            (s) => s.state_code === formData.country_of_birth_code,
          )[0]?.name;
          fData.birthplaceProvincia = 'EE';
        } else {
          fData.birthplace = municipalities.filter(
            (municipality) => municipality.cadastral_code === formData.commune_of_birth_code,
          )[0]?.name;
          fData.birthplaceProvincia = formData.province_of_birth_code;
        }
        formData.fiscal_code = generateFiscalCode(fData);
      }
      delete formData.attpr_value;
      commercialSubmitAnswers({ data: { ...formData } }, navigate, requestToken);
    }
  };

  useEffect(() => {
    backOfficeCommercialInitialData();

    return () => {
      resetCommercialForm();
    };
  }, []);

  useEffect(() => {
    const callAsync = async () => {
      setLoading(true);
      setIsModifyState(true);
      const {
        data: { data },
      } = await modifyRequestDataBackoffice(requestToken);
      setValue('attpr_value', data?.technical_location_data?.attpr_value);
      Object.keys(data).forEach((key) => {
        setValue(key, data[key], {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      });

      // The setValue calls above only touch top-level keys (e.g. 'address'),
      // not the leaf paths (e.g. 'address.address') isValid()/red-green
      // actually check — mark every known field across all steps touched +
      // validated now, regardless of which step is currently showing.
      await autoValidatePrefilledFields(
        Object.values(commercialStepFieldsById).flat(),
      );

      setIsModifyState(false);
      setLoading(false);
    };
    callAsync();
  }, []);

  useEffect(() => {
    if (formData.technical_location_data.annoc_value) {
      setValue(
        'technical_location_data.annor_value',
        formData.technical_location_data.annoc_value,
      );
    }
  }, [formData.technical_location_data.annoc_value]);

  useEffect(() => {
    if (formData.attpr_value) {
      setValue('technical_location_data.attpr_value', formData.attpr_value);
    }
  }, [formData.attpr_value]);

  const stepsQuote = [
    'Dati Aziendali',
    'Dati Tecnici',
    'Dati Contatto',
  ];

  if (loading) {
    return <PageLoading />;
  }

  if (isSubmitting === true) {
    return <QuoteGenerateLoading countNr={45} />;
  }

  return (
    <Layout>
      <V2FontImport />
      <div className="d-flex align-content-center justify-content-center mt-4">
        <ProgressBar activeStep={currentStepIndex + 1} steps={stepsQuote} />
      </div>
      <FormLayout className="form-container">{renderStepComponent()}</FormLayout>

      <FooterNav className="footer-buttons">
        <div className="d-flex justify-content-between">
          {step.id !== StepIdType.COMMERCIAL_COMPANY && (
            <SecondaryButton
              type="button"
              className="btn btn-questionnaire"
              onClick={() => {
                if (currentStepIndex > 0) {
                  setCurrentStepIndex(currentStepIndex - 1);
                }
              }}
            >
              Back
            </SecondaryButton>
          )}
          <PrimaryButton
            className="btn btn-questionnaire"
            type="button"
            onClick={() => handleNextStep()}
          >
            Continua
          </PrimaryButton>
        </div>
      </FooterNav>
    </Layout>
  );
}

export default BackOfficeCommercial;
